import { useMemo } from 'react';
import {
  useAutocompleteSearch,
  useStableOptions,
} from '@/hooks/useAutocompleteSearch';
import { useGetDriversQuery } from '@/pages/Business/pages/Drivers/services/drivers.service';
import { Option } from '@/components/inputs/AutocompleteInput';
import { driverAutocompleteLabel } from '../utils';

interface UseMonitoringDriversAutocompleteProps {
  search?: string;
  minLength?: number;
}

const useMonitoringDriversAutocomplete = ({
  search,
  minLength = 3,
}: UseMonitoringDriversAutocompleteProps = {}) => {
  const { debouncedSearch, shouldSearch, isDebouncePending } =
    useAutocompleteSearch(search, minLength);

  const {
    data,
    isFetching: isDriversFetching,
    error: driversError,
  } = useGetDriversQuery(
    {
      paginated: { page: 1, pageSize: 100 },
      filters: {
        search: shouldSearch ? debouncedSearch : '',
      },
    },
    { skip: !shouldSearch }
  );

  const isDriversLoading = shouldSearch && isDriversFetching;

  const resolvedDriverOptions: Option[] = useMemo(() => {
    if (!data?.items) return [];

    return data.items.map((driver) => ({
      label: driverAutocompleteLabel(driver),
      data: driver,
    }));
  }, [data?.items]);

  const driverOptions = useStableOptions(resolvedDriverOptions, {
    isPending: isDriversFetching || isDebouncePending,
    enabled: shouldSearch || isDebouncePending,
  });

  return {
    driverOptions,
    isDriversLoading,
    driversError,
  };
};

export default useMonitoringDriversAutocomplete;
